import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AnalysisScope, isWholeOrganisation, modelsInScope, scopeForLinqset } from '../analysis/scope';
import { Change, changeFormat, changeFrom, measure } from '../analysis/comparison';
import { ContextGraphButtonComponent } from '../analysis/context-graph-button.component';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { GraphData, Linqset, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { createInsightsFilter, DEFAULT_CURRENCY, getCurrency } from '../common/organisation';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { MODEL_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsService } from '../model-services/insights.service';
import { OrganisationService } from '../organisation/organisation.service';
import { FindingSeverity, WorkforceMatrix } from '../workforce/workforce-model';
import { AUTOMATION_LABELS, AutomationLevel, buildSystemsReport, OpportunityKind, SystemInterface, SystemsReport, SystemSummary } from './systems-model';

// the headline figures, as they're compared
const systemsFigures = (r: SystemsReport) => ({
    systems: measure(r.totals.systems, 'none', 'count'),
    cost: measure(r.totals.cost, 'down', 'money'),
    carbon: measure(r.totals.carbon, 'down', 'number'),
    automated: r.totals.automatedShare === null ? undefined : measure(r.totals.automatedShare * 100, 'up', 'percent'),
    manual: measure(r.totals.manualInterfaces, 'down', 'count'),
    friction: r.totals.frictionCost ? measure(r.totals.frictionCost, 'down', 'money') : measure(r.totals.frictionHours, 'down', 'hours'),
});

const COMPONENT = 'Systems report';

const SEVERITY: Record<FindingSeverity, { icon: string; label: string }> = {
    critical: { icon: 'error', label: 'Act now' },
    warning: { icon: 'warning', label: 'Look at' },
    info: { icon: 'info', label: 'Data' },
};

const OPPORTUNITY: Record<OpportunityKind, { icon: string; label: string }> = {
    integrate: { icon: 'sync_alt', label: 'Integrate' },
    automate: { icon: 'smart_toy', label: 'Automate' },
    digitise: { icon: 'edit_document', label: 'Digitise' },
    service: { icon: 'support_agent', label: 'Customer service' },
};

// the single-hue ramp the heatmaps use, light (little) to dark (most)
const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];

// Systems and infrastructure across the organisation: what they cost and emit, how much of the work is automated, how
// information moves between systems (by an interface or by people re-keying it), and where automation could free
// people's time. What needs attention comes first
@Component({
    selector: 'app-systems-report',
    standalone: true,
    imports: [DecimalPipe, NgTemplateOutlet, FormsModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSlideToggleModule, MatTooltipModule, ContextGraphButtonComponent],
    template: `
        @if (loading) {
        <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Building the systems report…</span></div>
        }@else if (error) {
        <p class="status error" role="alert">{{ error }}</p>
        }@else if (report; as r) {
        <div class="systems" data-testid="systems-report">
            <header class="intro">
                <h2>Systems &amp; infrastructure</h2>
                @if (r.scoped) {
                <p data-testid="systems-scope">The systems used in <strong>{{ scopeName || 'the models shown' }}</strong> and the infrastructure they run on: what they cost and
                    emit, the work they do or support there, how information moves between them, and where automation could free
                    people's time. Systems no work uses are only reported for the whole organisation.</p>
                }@else {
                <p>Every system and piece of infrastructure: what it costs and emits, the work it does or supports, how information
                    moves between systems, and where automation could free people's time. Built from the organisation model and
                    the work in every model.</p>
                }
            </header>

            <!-- the headline figures -->
            <section class="tiles" aria-label="Headline figures">
                <div class="tile"><span class="label"><mat-icon aria-hidden="true">dns</mat-icon>Systems</span><strong>{{ r.totals.systems | number }}</strong>@if (vs('systems'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ r.totals.aiAgents ? r.totals.aiAgents + ' AI ' + (r.totals.aiAgents === 1 ? 'agent' : 'agents') + ' · ' : '' }}{{ r.totals.infrastructure }} infrastructure</span></div>
                <div class="tile"><span class="label"><mat-icon aria-hidden="true">payments</mat-icon>Annual cost</span><strong>{{ money(r.totals.cost) }}</strong>@if (vs('cost'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}<span class="sub">systems and infrastructure</span></div>
                <div class="tile"><span class="label"><mat-icon aria-hidden="true">eco</mat-icon>Carbon a year</span><strong>{{ count(r.totals.carbon) }}</strong>@if (vs('carbon'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}<span class="sub">kg CO2e</span></div>
                <div class="tile" matTooltip="Actions done by systems alone, of all the actions a role or system does">
                    <span class="label"><mat-icon aria-hidden="true">smart_toy</mat-icon>Work automated</span><strong>{{ r.totals.automatedShare === null ? '–' : (r.totals.automatedShare * 100 | number: '1.0-0') + '%' }}</strong>@if (vs('automated'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ r.totals.actions.automated | number }} of {{ r.totals.actions.automated + r.totals.actions.assisted + r.totals.actions.manual | number }} actions</span></div>
                <div class="tile" [class.alert]="r.totals.manualInterfaceHours > 0" matTooltip="Interfaces where people take information into a system by hand, or out of systems. The hours are of the steps that do it, so the most an interface could save">
                    <span class="label"><mat-icon aria-hidden="true">keyboard</mat-icon>Manual interfaces</span><strong>{{ r.totals.manualInterfaces | number }}</strong>@if (vs('manual'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">in steps taking {{ hours(r.totals.manualInterfaceHours) }} hours a year</span></div>
                <div class="tile" [class.alert]="r.totals.frictionActions > 0" matTooltip="Friction work: exception work (steps on a detour from the main flow, or marked as such) and work marked as not adding value. Friction on the EPM scorecard; the systems table says which systems are involved">
                    <span class="label"><mat-icon aria-hidden="true">alt_route</mat-icon>Friction work</span>
                    <strong>{{ r.totals.frictionCost ? money(r.totals.frictionCost) : r.totals.frictionHours ? hours(r.totals.frictionHours) + ' h' : '–' }}</strong>@if (vs('friction'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ r.totals.frictionActions ? r.totals.frictionActions + (r.totals.frictionActions === 1 ? ' step, ' : ' steps, ') + r.totals.frictionAutomated + ' automated, ' + hours(r.totals.frictionHours) + ' hours a year' : 'no exception or wasted work' }}</span></div>
            </section>

            <!-- what to look at first -->
            <section class="panel" aria-labelledby="sy-findings">
                <div class="panel-head">
                    <h3 id="sy-findings">Look at these first</h3>
                    @if (r.findings.length) {
                    <span class="counts">
                        @for (c of severityCounts; track c.severity) {<span [class]="'pill ' + c.severity">{{ c.count }} {{ severity(c.severity).label.toLowerCase() }}</span>}
                    </span>
                    }
                </div>
                @if (r.findings.length) {
                <ul class="findings" data-testid="systems-findings">
                    @for (f of (allFindings ? r.findings : r.findings.slice(0, 5)); track $index) {
                    <li [class]="'finding ' + f.severity">
                        <mat-icon [attr.aria-label]="severity(f.severity).label">{{ severity(f.severity).icon }}</mat-icon>
                        <div><strong>{{ f.title }}</strong><p>{{ f.detail }}</p></div>
                        <span [class]="'pill ' + f.severity">{{ severity(f.severity).label }}</span>
                    </li>
                    }
                </ul>
                @if (r.findings.length > 5) {
                <button mat-button class="more" (click)="allFindings = !allFindings"><mat-icon>{{ allFindings ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allFindings ? 'Show fewer' : 'Show all ' + r.findings.length }}</button>
                }
                }@else {
                <p class="none">Nothing stands out: information moves between systems automatically and every system is used.</p>
                }
            </section>

            <!-- where automation could help -->
            <section class="panel" aria-labelledby="sy-opportunities">
                <h3 id="sy-opportunities">Where automation could help</h3>
                <p class="sy-hint">Steps where an interface, a system or automation could free people's time, largest first. Hours and
                    costs are of the steps involved ({{ hours(r.totals.opportunityHours) }} hours a year in all, each step counted once), so
                    the most there is to gain rather than a saving.</p>
                @if (r.opportunities.length) {
                <ol class="opportunities" data-testid="systems-opportunities">
                    @for (o of (allOpportunities ? r.opportunities : r.opportunities.slice(0, 8)); track $index) {
                    <li>
                        <span [class]="'kind ' + o.kind"><mat-icon aria-hidden="true">{{ opportunity(o.kind).icon }}</mat-icon>{{ opportunity(o.kind).label }}</span>
                        <div class="what"><strong class="with-context">{{ o.title }} @if (contextScope && o.actionKeys.length) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'action', key: o.actionKeys[0], name: o.title}}" [name]="o.title"></app-context-graph-button>}</strong><p>{{ o.detail }}</p><div class="models">@for (m of o.models; track m) {<span class="chip">{{ m }}</span>}</div></div>
                        <div class="size">@if (o.kind === 'service') {<strong>{{ hours(o.externalHours || 0) }} h</strong><span>of their time, not staff's</span>} @else {<strong>up to {{ hours(o.hours) }} h</strong>@if (o.cost > 0) {<span>about {{ money(o.cost) }}</span>}}</div>
                    </li>
                    }
                </ol>
                @if (r.opportunities.length > 8) {
                <button mat-button class="more" (click)="allOpportunities = !allOpportunities"><mat-icon>{{ allOpportunities ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allOpportunities ? 'Show fewer' : 'Show all ' + r.opportunities.length }}</button>
                }
                }@else {<p class="none">No manual interfaces, work outside systems or repetitive steps found.</p>}
            </section>

            <div class="two">
                <!-- how work is done -->
                <section class="panel" aria-labelledby="sy-automation">
                    <h3 id="sy-automation">How work is done</h3>
                    <p class="sy-hint">Actions by how they're done, {{ r.scoped ? 'in all' : 'across the organisation' }} and in each model (largest first).</p>
                    <div class="legend">@for (l of levels; track l) {<span><i [class]="'swatch ' + l"></i>{{ levelLabel(l) }}</span>}</div>
                    <ol class="stacks" data-testid="systems-automation">
                        <li class="overall">
                            <span class="name">All models</span>
                            <ng-container *ngTemplateOutlet="stack; context: { a: r.totals.actions }"></ng-container>
                            <span class="value">{{ r.totals.actions.automated + r.totals.actions.assisted + r.totals.actions.manual + r.totals.actions.unassigned | number }}</span>
                        </li>
                        @for (m of (allModels ? r.models : r.models.slice(0, 12)); track m.key) {
                        <li>
                            <span class="name" [title]="m.name">{{ m.name }}</span>
                            <ng-container *ngTemplateOutlet="stack; context: { a: m.actions }"></ng-container>
                            <span class="value">{{ m.total | number }}</span>
                        </li>
                        }
                    </ol>
                    @if (r.models.length > 12) {
                    <button mat-button class="more" (click)="allModels = !allModels"><mat-icon>{{ allModels ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allModels ? 'Show fewer' : 'Show all ' + r.models.length + ' models' }}</button>
                    }
                </section>

                <!-- cost and carbon -->
                <section class="panel" aria-labelledby="sy-cost">
                    <h3 id="sy-cost">Where the money goes</h3>
                    <p class="sy-hint">Each system's annual cost: its own, and its share of the infrastructure it runs on.</p>
                    <div class="legend"><span><i class="swatch own"></i>Own cost</span><span><i class="swatch infra"></i>Infrastructure</span></div>
                    @if (costSystems.length) {
                    <ol class="bars" data-testid="systems-cost">
                        @for (s of costSystems; track s.key) {
                        <li [matTooltip]="s.name + ': ' + money(s.ownCost) + ' own, ' + money(s.infrastructureCost) + ' infrastructure, ' + count(s.carbon) + ' kg CO2e a year'">
                            <span class="name">{{ s.name }}</span>
                            <span class="track">
                                <span class="fill own" [style.width.%]="costWidth(s.ownCost)"></span>
                                <span class="fill infra" [style.left.%]="costWidth(s.ownCost)" [style.width.%]="costWidth(s.infrastructureCost)"></span>
                            </span>
                            <span class="value">{{ money(s.cost) }}</span>
                        </li>
                        }
                    </ol>
                    }@else {<p class="none">No system has a cost set.</p>}
                </section>
            </div>

            <!-- the value the work each system is used on creates -->
            <section class="panel" aria-labelledby="sy-value">
                <h3 id="sy-value">The value each system helps create</h3>
                <p class="sy-hint">Each system's share of the value and revenue the work it's used on creates. Each output's value is shared between the steps producing it by their work, and each step's share between the systems used on it, so these add up. Only value adding work creates any. Revenue return is the revenue it helps create for each {{ currencySymbol }}1 of its cost allocated to that work.</p>
                @if (valueSystems.length) {
                <div class="scroll">
                    <table class="list values" data-testid="systems-value">
                        <thead><tr><th>System</th><th class="num">Value contribution</th><th class="num">Revenue contribution</th><th class="num">Cost of its work</th><th class="num">Revenue return</th></tr></thead>
                        <tbody>
                            @for (s of valueSystems; track s.key) {
                            <tr>
                                <td class="first">{{ s.name }}@if (s.aiAgent) {<span class="tag ai">AI agent</span>}</td>
                                <td class="num">{{ count(s.contribution.value) }}</td>
                                <td class="num">{{ s.contribution.revenue ? money(s.contribution.revenue) : '–' }}</td>
                                <td class="num">{{ s.contribution.workCost ? money(s.contribution.workCost) : '–' }}</td>
                                <td class="num" [class.low]="s.contribution.revenueReturn !== null && s.contribution.revenueReturn < 1">{{ s.contribution.revenueReturn === null ? '–' : (s.contribution.revenueReturn | number: '1.1-2') + '×' }}</td>
                            </tr>
                            }
                        </tbody>
                        <tfoot><tr><td>All systems</td><td class="num">{{ count(r.totals.valueContribution) }}</td><td class="num">{{ r.totals.revenueContribution ? money(r.totals.revenueContribution) : '–' }}</td><td class="num">{{ r.totals.workCost ? money(r.totals.workCost) : '–' }}</td>
                            <td class="num">{{ r.totals.workCost && r.totals.revenueContribution ? (r.totals.revenueContribution / r.totals.workCost | number: '1.1-2') + '×' : '–' }}</td></tr></tfoot>
                    </table>
                </div>
                }@else {
                <p class="none">No system is used on work that creates value or revenue yet: set a value or revenue on the outputs or outcomes the work leads to.</p>
                }
            </section>

            <!-- interfaces -->
            <section class="panel" aria-labelledby="sy-interfaces">
                <div class="panel-head">
                    <h3 id="sy-interfaces">How information moves between systems</h3>
                    <mat-slide-toggle [(ngModel)]="manualOnly" data-testid="systems-manual-only">Only manual</mat-slide-toggle>
                </div>
                <p class="sy-hint">{{ count(r.totals.interfaceItems) }} items a year pass between systems,
                    {{ r.totals.interfaceItems ? (r.totals.automatedInterfaceItems / r.totals.interfaceItems * 100 | number: '1.0-0') : 0 }}% of them
                    by an automated interface: to a step no one does, or typed as held in a system (a database, an application or a service).
                    The rest people move: re-keying, uploading a file, or entering information that arrived on paper, by word of mouth or outside systems.</p>
                <div class="scroll">
                    <table class="list" data-testid="systems-interfaces">
                        <thead><tr><th>From</th><th>To</th><th>Automated</th><th class="num">Items a year</th><th class="num" matTooltip="People's time in the steps that take the information in by hand">Hours in those steps</th><th class="num">About</th><th>Where</th></tr></thead>
                        <tbody>
                            @for (i of shownInterfaces; track i.fromKey + i.toKey) {
                            <tr>
                                <td class="first"><span class="with-context">{{ i.from }}@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{node: i.fromKey}" [name]="i.from"></app-context-graph-button>}</span></td><td class="first"><span class="with-context">{{ i.to }}@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{node: i.toKey}" [name]="i.to"></app-context-graph-button>}</span></td>
                                <td class="nowrap"><span class="mini"><span class="fill automated" [style.width.%]="automatedShare(i) * 100"></span></span>
                                    <span class="mini-value">{{ automatedShare(i) * 100 | number: '1.0-0' }}%</span></td>
                                <td class="num">{{ count(i.items) }}</td>
                                <td class="num">{{ i.manualHours ? hours(i.manualHours) : '–' }}</td>
                                <td class="num">{{ i.manualCost ? money(i.manualCost) : '–' }}</td>
                                <td class="where">{{ i.actions.slice(0, 2).join(', ') }}{{ i.actions.length > 2 ? ' and ' + (i.actions.length - 2) + ' more' : '' }}</td>
                            </tr>
                            }@empty {
                            <tr><td colspan="7" class="none">{{ r.interfaces.length ? 'Every interface is automated.' : 'No information passes between systems in the models.' }}</td></tr>
                            }
                        </tbody>
                    </table>
                </div>
                @if (filteredInterfaces.length > 15) {
                <button mat-button class="more" (click)="allInterfaces = !allInterfaces"><mat-icon>{{ allInterfaces ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allInterfaces ? 'Show fewer' : 'Show all ' + filteredInterfaces.length }}</button>
                }
                @if (r.interfaceMatrix.rows.length > 1) {
                <h4 class="matrix-title">Items a year from each system (rows) to each other (columns)</h4>
                <ng-container *ngTemplateOutlet="heatmap; context: { m: r.interfaceMatrix }"></ng-container>
                }
            </section>

            <!-- outputs -->
            <section class="panel" aria-labelledby="sy-outputs">
                <h3 id="sy-outputs">How each output is produced</h3>
                <p class="sy-hint">Every step on the way to each output, back to where the work starts: how the steps are done, the
                    systems involved and the places people move information between systems by hand. Most people's time first.</p>
                <div class="legend">@for (l of levels; track l) {<span><i [class]="'swatch ' + l"></i>{{ levelLabel(l) }}</span>}</div>
                <div class="scroll">
                    <table class="list" data-testid="systems-outputs">
                        <thead><tr><th>Output</th><th class="num">Items a year</th><th>Steps</th><th class="num">Automated</th><th class="num">People's hours</th>
                            <th class="num" matTooltip="Places on the way where people move information into a system, or out of systems">Manual hand-offs</th><th>Systems</th></tr></thead>
                        <tbody>
                            @for (o of (allOutputs ? r.outputs : r.outputs.slice(0, 15)); track o.key) {
                            <tr>
                                <td class="first"><span class="with-context">{{ o.name }}@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'output', key: o.key, name: o.name}}" [name]="o.name"></app-context-graph-button>}</span><span class="model">{{ o.model }}</span></td>
                                <td class="num">{{ count(o.items) }}</td>
                                <td class="nowrap"><span class="steps"><ng-container *ngTemplateOutlet="stack; context: { a: o.actions }"></ng-container></span>
                                    <span class="mini-value">{{ o.steps }}</span></td>
                                <td class="num">{{ o.automatedShare === null ? '–' : (o.automatedShare * 100 | number: '1.0-0') + '%' }}</td>
                                <td class="num">{{ hours(o.hours) }}</td>
                                <td class="num">{{ o.manualHandoffs | number }}</td>
                                <td>@for (s of o.systems.slice(0, 3); track s) {<span class="chip">{{ s }}</span>}@empty {<span class="muted">None</span>}@if (o.systems.length > 3) {<span class="more-count">+{{ o.systems.length - 3 }}</span>}</td>
                            </tr>
                            }@empty {<tr><td colspan="7" class="none">No outputs in the models.</td></tr>}
                        </tbody>
                    </table>
                </div>
                @if (r.outputs.length > 15) {
                <button mat-button class="more" (click)="allOutputs = !allOutputs"><mat-icon>{{ allOutputs ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allOutputs ? 'Show fewer' : 'Show all ' + r.outputs.length + ' outputs' }}</button>
                }
            </section>

            <!-- systems -->
            <section class="panel" aria-labelledby="sy-systems">
                <h3 id="sy-systems">Systems</h3>
                <div class="scroll">
                    <table class="list" data-testid="systems-table">
                        <thead><tr><th>System</th><th class="num">Cost a year</th><th class="num">kg CO2e</th><th class="num">Automated</th><th class="num">With people</th>
                            <th class="num" matTooltip="Actions involving it that are friction work: exception work (steps on a detour from the main flow, or marked as such) and work marked as not adding value">Friction work</th>
                            <th class="num">Roles</th><th class="num">Outputs</th><th>Models</th><th>Runs on</th><th class="num">Interfaces in / out</th></tr></thead>
                        <tbody>
                            @for (s of (allSystems ? r.systems : r.systems.slice(0, 20)); track s.key) {
                            <tr [class.unused]="!s.used">
                                <td class="first"><span class="with-context">{{ s.name }}@if (contextScope && s.used) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'system', key: s.key, name: s.name}}" [name]="s.name"></app-context-graph-button>}</span>@if (s.aiAgent) {<span class="tag ai">AI agent</span>}@if (!s.used) {<span class="tag">No modelled work</span>}</td>
                                <td class="num">{{ s.cost ? money(s.cost) : '–' }}</td>
                                <td class="num">{{ s.carbon ? count(s.carbon) : '–' }}</td>
                                <td class="num">{{ s.automatedActions | number }}</td>
                                <td class="num">{{ s.assistedActions | number }}</td>
                                <td class="num" [class.exception]="s.frictionActions > 0">{{ s.frictionActions ? (s.frictionCost ? money(s.frictionCost) : hours(s.frictionHours) + ' h') + ' (' + s.frictionActions + ')' : '–' }}</td>
                                <td class="num">{{ s.roles | number }}</td>
                                <td class="num">{{ s.outputs | number }}</td>
                                <td class="where" [title]="s.models.join(', ')">{{ s.models.length ? (s.models.length === 1 ? s.models[0] : s.models.length + ' models') : '–' }}</td>
                                <td>@for (i of s.infrastructure; track i) {<span class="chip">{{ i }}</span>}@empty {–}</td>
                                <td class="num">{{ s.interfacesIn }} / {{ s.interfacesOut }}</td>
                            </tr>
                            }@empty {<tr><td colspan="11" class="none">There are no systems in the organisation model.</td></tr>}
                        </tbody>
                    </table>
                </div>
                @if (r.systems.length > 20) {
                <button mat-button class="more" (click)="allSystems = !allSystems"><mat-icon>{{ allSystems ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allSystems ? 'Show fewer' : 'Show all ' + r.systems.length + ' systems' }}</button>
                }
            </section>

            <!-- infrastructure -->
            <section class="panel" aria-labelledby="sy-infrastructure">
                <h3 id="sy-infrastructure">Infrastructure</h3>
                <div class="scroll">
                    <table class="list" data-testid="systems-infrastructure">
                        <thead><tr><th>Infrastructure</th><th>Kind</th><th class="num">Cost a year</th><th class="num">kg CO2e</th><th>Systems it runs</th></tr></thead>
                        <tbody>
                            @for (i of r.infrastructure; track i.key) {
                            <tr>
                                <td class="first">{{ i.name }}</td>
                                <td class="nowrap">{{ i.kind === 'cloud' ? 'Cloud' : i.kind === 'on-premise' ? 'On premise' : 'Other' }}</td>
                                <td class="num">{{ i.cost ? money(i.cost) : '–' }}</td>
                                <td class="num">{{ i.carbon ? count(i.carbon) : '–' }}</td>
                                <td>@for (s of i.systems; track s.key) {<span class="chip" [class.idle]="!s.used" [matTooltip]="s.used ? '' : 'No modelled work uses it'">{{ s.name }}</span>}@empty {<span class="none">None</span>}</td>
                            </tr>
                            }@empty {<tr><td colspan="5" class="none">There is no infrastructure in the organisation model.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>

        <ng-template #stack let-a="a">
            <span class="track stack">
                @for (l of levels; track l) {
                @if (a[l]) {<span [class]="'part ' + l" [style.flex-grow]="a[l]" [matTooltip]="levelLabel(l) + ': ' + a[l] + ' actions'"></span>}
                }
            </span>
        </ng-template>

        <ng-template #heatmap let-m="m">
            <div class="scroll">
                <table class="heatmap" aria-label="Table of items a year between systems">
                    <thead><tr><th class="corner"></th>@for (c of m.columns; track c.key) {<th scope="col"><span [title]="c.name">{{ c.name }}</span></th>}</tr></thead>
                    <tbody>
                        @for (row of m.rows; track row.key; let i = $index) {
                        <tr>
                            <th scope="row"><span [title]="row.name">{{ row.name }}</span></th>
                            @for (c of m.columns; track c.key; let j = $index) {
                            <td [style.background]="cellColour(m, m.values[i][j])" [class.dark]="isDark(m, m.values[i][j])" [class.empty]="!m.values[i][j]"
                                [matTooltip]="m.values[i][j] ? row.name + ' → ' + c.name + ': ' + count(m.values[i][j]) + ' items a year' : ''">
                                {{ m.values[i][j] ? count(m.values[i][j]) : '' }}</td>
                            }
                        </tr>
                        }
                    </tbody>
                </table>
            </div>
        </ng-template>
        }
    `,
    styles: [`
        .with-context { display: inline-flex; align-items: center; gap: 2px; }
        /* the app's palette, as the workforce report has: teal for automated work and money, amber and red for work people do */
        :host { display: block; --sy-automated: #00a9a5; --sy-assisted: #f0b429; --sy-manual: #d64541; --sy-external: #8e6fc1; --sy-unassigned: #a9aebb;
            --sy-own: #00a9a5; --sy-infra: #8fd6d3; --sy-ink: var(--ink, #1E2233); --sy-muted: var(--slate, #5b6172); --sy-grey: var(--grey, #9aa0b0);
            --sy-line: var(--line, #e7e9f0); --sy-bg: var(--bg, #f4f5f9); --sy-surface: var(--paper, #fff); --sy-red: #b42318; }
        .status { display: flex; align-items: center; gap: 10px; margin: 16px; color: var(--sy-muted); }
        .error { color: var(--sy-red); }
        .systems { display: flex; flex-direction: column; gap: 16px; padding: 12px 16px 24px; color: var(--sy-ink); max-width: 1400px; container-type: inline-size; container-name: systems; }
        h2 { margin: 0 0 4px; font-size: 20px; font-weight: 600; }
        h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
        .intro p { margin: 0; color: var(--sy-muted); font-size: 13px; line-height: 1.5; max-width: 90ch; }
        .intro strong { color: var(--sy-ink); font-weight: 600; }
        .sy-hint { margin: 0 0 10px; color: var(--sy-muted); font-size: 12.5px; line-height: 1.45; max-width: 90ch; }
        .none { color: var(--sy-muted); font-size: 13px; }
        .muted { color: var(--sy-grey); }

        /* sections are cards, as on the overview */
        .panel { min-width: 0; padding: 14px 16px 12px; border: 1px solid var(--sy-line); border-radius: 10px; background: var(--sy-surface); }
        .panel-head { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap; margin-bottom: 8px; }
        .panel-head h3 { margin: 0; }
        .more { margin-top: 6px; }
        .more mat-icon { margin-right: 2px; }
        .matrix-title { margin: 16px 0 6px; font-size: 13px; font-weight: 600; color: var(--sy-muted); }

        /* the headline figures: five to a line when there's room, three then two on six tracks, else two with the last across both */
        .tiles { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
        @container systems (max-width: 860px) {
            .tiles { grid-template-columns: repeat(6, minmax(0, 1fr)); }
            .tile { grid-column: span 2; }
            .tile:nth-child(n+4):nth-last-child(-n+2) { grid-column: span 3; }
        }
        @container systems (max-width: 520px) {
            .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .tile, .tile:nth-child(n+4):nth-last-child(-n+2) { grid-column: auto; }
            .tile:last-child:nth-child(odd) { grid-column: 1 / -1; }
        }
        .tile { display: flex; flex-direction: column; gap: 2px; min-width: 0; padding: 12px 14px; border: 1px solid var(--sy-line); border-radius: 10px; background: var(--sy-surface); }
        .tile .label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--sy-muted); }
        .tile .label mat-icon { width: 16px; height: 16px; font-size: 16px; color: var(--sy-grey); }
        /* a project's figure against the current state's */
        .chg { align-self: flex-start; margin-top: 4px; font-size: 11px; font-weight: 700; white-space: nowrap; padding: 1px 6px; border-radius: 6px; }
        .chg.good { background: #e4f6ec; color: #1e7b45; }
        .chg.bad { background: #fbe4e2; color: #b42318; }
        .chg.flat { background: #eef0f6; color: #6b7280; }
        .tile strong { font-size: 22px; font-weight: 600; line-height: 1.25; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
        .tile .sub { font-size: 12px; color: var(--sy-muted); line-height: 1.35; }
        .tile.alert { border-color: #f6dca4; background: #fffaf0; }
        .tile.alert .label mat-icon { color: #c98500; }

        /* severity, as a small pill: act now in red, look at in amber, data in grey */
        .counts { display: flex; flex-wrap: wrap; gap: 6px; }
        .pill { flex: none; padding: 1px 8px; border-radius: 10px; font-size: 11.5px; font-weight: 600; white-space: nowrap; background: var(--sy-bg); color: var(--sy-muted); }
        .pill.critical { background: #fde8e8; color: var(--sy-red); }
        .pill.warning { background: #fdf3dd; color: #8a5a00; }

        /* findings: a compact list, marked by an edge in their severity's colour */
        .findings { list-style: none; margin: 0; padding: 0; }
        .finding { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 10px; align-items: start; padding: 9px 10px 9px 8px; border-left: 3px solid var(--sy-unassigned); border-bottom: 1px solid var(--sy-line); }
        .finding:last-child { border-bottom: 0; }
        .finding mat-icon { width: 20px; height: 20px; font-size: 20px; margin-top: 1px; color: var(--sy-grey); }
        .finding.critical { border-left-color: var(--sy-manual); } .finding.critical mat-icon { color: var(--sy-manual); }
        .finding.warning { border-left-color: #f0b429; } .finding.warning mat-icon { color: #c98500; }
        .finding strong { display: block; font-size: 13.5px; font-weight: 600; line-height: 1.35; }
        .finding p { margin: 2px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--sy-muted); }
        @container systems (max-width: 520px) { .finding > .pill { display: none; } }

        /* opportunities: what to do, what it's about, and the most there is to gain */
        .opportunities { list-style: none; margin: 0; padding: 0; }
        .opportunities li { display: grid; grid-template-columns: 112px minmax(0, 1fr) 116px; gap: 12px; align-items: start; padding: 10px 4px; border-bottom: 1px solid var(--sy-line); }
        .opportunities li:last-child { border-bottom: 0; }
        .opportunities strong { font-size: 13.5px; font-weight: 600; }
        .opportunities p { margin: 2px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--sy-muted); overflow-wrap: anywhere; }
        .opportunities .models { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
        .kind { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 10px; font-size: 12px; font-weight: 600; width: fit-content; }
        .kind mat-icon { font-size: 15px; width: 15px; height: 15px; }
        .kind.integrate { background: #fdf3dd; color: #8a5a00; } .kind.automate { background: var(--teal-soft, #e0f5f4); color: #00706d; } .kind.digitise { background: #eef1f7; color: #3b4a6b; }
        .size { display: flex; flex-direction: column; align-items: flex-end; text-align: right; font-variant-numeric: tabular-nums; }
        .size strong { white-space: nowrap; }
        .size span { font-size: 12px; color: var(--sy-muted); }
        @container systems (max-width: 640px) {
            .opportunities li { grid-template-columns: minmax(0, 1fr) auto; }
            .opportunities .what { grid-column: 1 / -1; grid-row: 2; }
            .size { flex-direction: row; gap: 6px; align-items: baseline; }
        }

        /* the two bar charts, side by side when the report itself is wide enough, whatever the window */
        .two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        @container systems (max-width: 900px) { .two { grid-template-columns: minmax(0, 1fr); } }
        .legend { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 12px; color: var(--sy-muted); margin-bottom: 10px; }
        .legend span { display: inline-flex; align-items: center; gap: 5px; }
        .swatch { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
        .bars, .stacks { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
        .bars li, .stacks li { display: grid; grid-template-columns: minmax(90px, 36%) minmax(0, 1fr) 56px; align-items: center; gap: 10px; min-height: 26px; padding: 0 4px; border-radius: 6px; font-size: 13px; }
        .bars li:hover, .stacks li:hover { background: var(--sy-bg); }
        .stacks li.overall { font-weight: 600; padding-bottom: 6px; margin-bottom: 4px; border-bottom: 1px solid var(--sy-line); border-radius: 6px 6px 0 0; }
        .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .value { text-align: right; font-variant-numeric: tabular-nums; color: var(--sy-muted); font-size: 12.5px; }
        .track { position: relative; height: 10px; background: var(--sy-bg); border-radius: 5px; }
        .track .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 5px; }
        .track .fill.infra { border-left: 2px solid var(--sy-surface); }
        .stack { display: flex; gap: 2px; background: transparent; overflow: hidden; }
        .stack .part { flex-basis: 0; min-width: 2px; height: 100%; }
        .stack .part:first-child { border-radius: 5px 0 0 5px; }
        .stack .part:last-child { border-radius: 0 5px 5px 0; }
        .stack .part:only-child { border-radius: 5px; }
        .automated { background: var(--sy-automated); } .assisted { background: var(--sy-assisted); } .manual { background: var(--sy-manual); } .external { background: var(--sy-external); } .unassigned { background: var(--sy-unassigned); }
        .fill.own, .swatch.own { background: var(--sy-own); } .fill.infra, .swatch.infra { background: var(--sy-infra); }

        /* tables: the quiet style the other insights tables use */
        .scroll { overflow-x: auto; max-width: 100%; }
        table { border-collapse: collapse; font-size: 13px; }
        .list { width: 100%; min-width: 760px; }
        .list th { height: 36px; padding: 0 10px; background: var(--sy-bg); border-bottom: 1px solid var(--sy-line); color: var(--sy-muted);
            font-size: 11.5px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; text-align: left; white-space: nowrap; }
        .list th.num { text-align: right; }
        .list td { height: 40px; padding: 4px 10px; border-bottom: 1px solid var(--sy-line); vertical-align: middle; }
        .list tbody tr:hover td { background: #f8f9fb; }
        .list tbody tr:last-child td { border-bottom: 0; }
        .list td.first { min-width: 140px; font-weight: 500; }
        .list tr.unused td { color: var(--sy-muted); }
        .list tr.unused td.first { font-weight: 400; }
        .values tfoot td { height: 36px; padding: 4px 10px; border-top: 1px solid var(--sy-line); font-weight: 600; }
        .values td.low { color: var(--sy-red); }
        .where { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--sy-muted); }
        td.exception { color: #8a5a00; font-weight: 600; }
        .nowrap { white-space: nowrap; }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .chip, .tag { display: inline-flex; align-items: center; margin: 1px 4px 1px 0; padding: 1px 8px; border-radius: 6px; font-size: 12px; line-height: 1.6; white-space: nowrap; }
        .chip { background: var(--sy-bg); border: 1px solid var(--sy-line); color: var(--sy-ink); }
        .chip.idle { background: transparent; border: 1px dashed #b8bdc9; color: var(--sy-muted); }
        .tag { margin-left: 6px; border-radius: 10px; background: #fdf3dd; color: #8a5a00; font-weight: 500; }
        .tag.ai { background: var(--teal-soft, #e0f5f4); color: #00706d; }
        .mini { display: inline-block; position: relative; width: 64px; height: 8px; background: var(--sy-bg); border-radius: 4px; vertical-align: middle; overflow: hidden; }
        .mini .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 4px; }
        .mini-value { margin-left: 8px; font-variant-numeric: tabular-nums; }
        .model { display: block; font-size: 12px; font-weight: 400; color: var(--sy-muted); }
        .steps { display: inline-block; width: 90px; vertical-align: middle; }
        .steps .track { height: 8px; }
        .more-count { font-size: 12px; color: var(--sy-muted); }

        /* the heatmap: names kept in view while the grid scrolls, column names over two lines, empty cells faintly marked */
        .heatmap { border-collapse: separate; border-spacing: 3px; margin: 0 -3px; }
        .heatmap th { font-weight: 500; font-size: 12px; color: var(--sy-muted); padding: 2px 4px; }
        .heatmap thead th { vertical-align: bottom; min-width: 56px; max-width: 120px; }
        .heatmap thead th span { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.25; text-align: center; }
        .heatmap tbody th { position: sticky; left: 0; z-index: 1; text-align: left; color: var(--sy-ink); background: var(--sy-surface); }
        .heatmap thead th.corner { position: sticky; left: 0; z-index: 1; background: var(--sy-surface); }
        .heatmap tbody th span { display: block; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .heatmap td { min-width: 56px; height: 30px; text-align: center; font-size: 11.5px; font-variant-numeric: tabular-nums; border-radius: 5px; color: #0b0b0b; }
        .heatmap td.empty { background: #f7f8fa !important; }
        .heatmap td.dark { color: #fff; }

        /* very narrow (the insights panel beside a model): less padding, and each name above its bar */
        @container systems (max-width: 380px) {
            .panel { padding: 12px; }
            .bars, .stacks { gap: 6px; }
            .bars li, .stacks li { grid-template-columns: minmax(0, 1fr) 48px; gap: 2px 8px; padding: 2px 0; }
            .bars li .name, .stacks li .name { grid-column: 1 / -1; font-size: 12.5px; }
            .tile { padding: 10px 12px; }
            .tile strong { font-size: 19px; }
            .panel .heatmap tbody th span { max-width: 96px; }
            .opportunities li { grid-template-columns: minmax(0, 1fr); gap: 6px; }
            .opportunities .what { grid-row: 2; }
            .size { grid-row: 3; flex-wrap: wrap; align-items: baseline; text-align: left; }
        }
`],
})
export class SystemsReportComponent implements OnChanges {
    @Input() linqset: Linqset;
    @Input() trigger: any;                           // the insights were refreshed
    @Input() prebuilt?: SystemsReport;
    @Input() baseline?: SystemsReport;               // in a project, the same report for the current state, which its figures are compared with

    // the models looked at, for the context graphs of its systems, steps and outputs
    get contextScope(): AnalysisScope | undefined {
        return this.linqset ? scopeForLinqset(this.linqset) : undefined;
    }               // a report already built, as the Systems lens has, so it isn't built twice

    readonly levels: AutomationLevel[] = ['automated', 'assisted', 'manual', 'external', 'unassigned'];
    report?: SystemsReport;
    loading = false;
    error = '';
    allFindings = false;
    allOpportunities = false;
    allModels = false;
    allSystems = false;
    allInterfaces = false;
    allOutputs = false;
    manualOnly = false;
    costSystems: SystemSummary[] = [];
    valueSystems: SystemSummary[] = [];              // systems used on work that creates value or revenue, the most first
    scopeName = '';                                  // the model, or how many models, the report is for when it isn't the whole organisation
    severityCounts: { severity: FindingSeverity; count: number }[] = [];   // how many findings of each severity, most severe first
    private maxCost = 0;
    private request = 0;

    constructor(private organisationService: OrganisationService, private insightsService: InsightsService, private repoService: RepositoryService,
        private formatter: FormattingService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.prebuilt) {
            if (changes['prebuilt']) this.show(this.prebuilt);
            return;
        }
        if (changes['linqset'] || changes['trigger']) void this.load();
    }

    // the systems and infrastructure behind what's being looked at: a model being edited, the models the insights are
    // filtered to, or the whole organisation. As the analysis views do, a scope keeps just the work in its models
    private async load() {
        const organisation = this.organisationService.organisation;
        if (!organisation || !this.linqset) return;
        const workspaceKey = this.linqset.workspaceKey;
        const scope = scopeForLinqset(this.linqset);
        const scoped = !isWholeOrganisation(scope);
        const models = scoped ? modelsInScope(scope, organisation) : [];
        const modelKeys = new Set(models.map(m => m._key));
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            const [analysis, organisationModel] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, createInsightsFilter(workspaceKey)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
                this.insightsService.afterRefresh(workspaceKey, () => this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, ORGANISATION_MODEL_KEY, workspaceKey)), COMPONENT, 'Load the organisation model')),
            ]);
            if (request !== this.request) return;
            const inScope = scoped ? { nodes: analysis.nodes.filter(n => modelKeys.has(n.modelKey)), edges: analysis.edges.filter(e => modelKeys.has(e.modelKey)) } : analysis;
            this.scopeName = !scoped ? '' : models.length === 1 ? models[0].name : `${models.length} models`;
            this.show(buildSystemsReport({
                organisationModel, analysis: inScope, workspaceKey, scoped,
                modelNames: Object.fromEntries(organisation.models.map(m => [m._key, m.name])),
                money: v => this.money(v),
                number: v => this.formatter.formatAbbreviatedNumber(v),
            }));
        }catch(err) {
            if (request !== this.request) return;
            console.error('Unable to build the systems report', err);
            this.error = "The systems report couldn't be built. Please try again.";
        }finally {
            if (request === this.request) this.loading = false;
        }
    }

    private show(report: SystemsReport) {
        this.report = report;
        this.severityCounts = (['critical', 'warning', 'info'] as FindingSeverity[])
            .map(severity => ({ severity, count: report.findings.filter(f => f.severity === severity).length })).filter(c => c.count > 0);
        this.costSystems = report.systems.filter(s => s.cost > 0).slice(0, 12);
        this.maxCost = Math.max(0, ...this.costSystems.map(s => s.cost));
        this.valueSystems = report.systems.filter(s => s.contribution.value > 0 || s.contribution.revenue > 0)
            .sort((a, b) => b.contribution.revenue - a.contribution.revenue || b.contribution.value - a.contribution.value);
    }

    get currencySymbol() {
        return getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol || '';
    }

    get filteredInterfaces(): SystemInterface[] {
        const all = this.report?.interfaces || [];
        return this.manualOnly ? all.filter(i => i.items - i.automatedItems > 0.5) : all;
    }

    get shownInterfaces(): SystemInterface[] {
        return this.allInterfaces ? this.filteredInterfaces : this.filteredInterfaces.slice(0, 15);
    }

    automatedShare(i: SystemInterface) {
        return i.items ? i.automatedItems / i.items : 0;
    }

    // a headline figure against the current state's, when a project is looked at
    vs(figure: keyof ReturnType<typeof systemsFigures>): Change | undefined {
        if (!this.baseline || !this.report) return undefined;
        return changeFrom(systemsFigures(this.baseline)[figure], systemsFigures(this.report)[figure], changeFormat(v => this.money(v), v => this.count(v)));
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY));
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    count(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    severity(s: FindingSeverity) {
        return SEVERITY[s];
    }

    opportunity(k: OpportunityKind) {
        return OPPORTUNITY[k];
    }

    levelLabel(l: AutomationLevel) {
        return AUTOMATION_LABELS[l];
    }

    costWidth(value: number) {
        return this.maxCost ? Math.max(0, value) / this.maxCost * 100 : 0;
    }

    // a cell's shade on the ramp, by its share of the largest (square-rooted, so smaller flows still show)
    private step(m: WorkforceMatrix, value: number) {
        return value && m.max ? Math.min(RAMP.length - 1, Math.floor(Math.sqrt(value / m.max) * RAMP.length)) : -1;
    }

    cellColour(m: WorkforceMatrix, value: number) {
        const step = this.step(m, value);
        return step < 0 ? 'transparent' : RAMP[step];
    }

    isDark(m: WorkforceMatrix, value: number) {
        return this.step(m, value) >= 3;
    }
}
